if (!customElements.get('home-transformations')) {
  customElements.define(
    'home-transformations',
    class HomeTransformations extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-transformations-viewport]');
        this.track = this.querySelector('[data-transformations-track]');
        this.slides = Array.from(this.querySelectorAll('[data-transformations-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-transformations-dot]'));
        this.count = Number(this.dataset.count);

        if (!this.viewport || !this.track || this.count < 2) return;

        this.index = this.count;
        this.lastRenderedIndex = undefined;
        this.cachedSlideWidth = 0;
        this.dragOffset = 0;
        this.dragging = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.querySelector('[data-transformations-previous]')?.addEventListener('click', () => this.move(-1));
        this.querySelector('[data-transformations-next]')?.addEventListener('click', () => this.move(1));
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goTo(Number(dot.dataset.transformationsDot)));
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.move(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.move(1);
          }
        });

        this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
        this.track.addEventListener('transitionend', (event) => {
          if (event.target === this.track && event.propertyName === 'transform') this.normalizeLoop();
        });

        this.onViewportResize = () => {
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(() => {
            if (!this.isConnected) return;
            this.measure();
            this.render(false);
          });
        };
        this.resizeObserver = new ResizeObserver(this.onViewportResize);
        this.resizeObserver.observe(this.viewport);
        // A viewport breakpoint can change per-view CSS even when a max-width
        // viewport element itself remains the same size.
        window.addEventListener('resize', this.onViewportResize);

        requestAnimationFrame(() => {
          this.classList.add('is-ready');
          this.measure();
          this.render(false);
        });
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.resizeFrame);
        window.removeEventListener('resize', this.onViewportResize);
      }

      get slidesPerView() {
        return parseFloat(getComputedStyle(this).getPropertyValue('--transformations-per-view')) || 1.2;
      }

      measure() {
        this.viewportWidth = this.viewport.clientWidth;
        this.cachedSlidesPerView = this.slidesPerView;
        this.cachedSlideWidth = this.viewportWidth / this.cachedSlidesPerView;
        this.style.setProperty('--transformations-slide-width', `${this.cachedSlideWidth}px`);
      }

      get slideWidth() {
        return this.cachedSlideWidth || this.viewport.clientWidth / (this.cachedSlidesPerView || 1.2);
      }

      get realIndex() {
        return ((this.index % this.count) + this.count) % this.count;
      }

      move(direction) {
        if (this.dragging) return;
        this.index += direction;
        this.render(true);
      }

      goTo(realIndex) {
        this.index = this.count + realIndex;
        this.render(true);
      }

      render(animate = true) {
        if (!this.cachedSlideWidth) this.measure();
        const offset = this.viewportWidth / 2 - this.cachedSlideWidth / 2 - this.index * this.cachedSlideWidth;
        this.track.style.transition = animate && !this.reducedMotion ? 'transform 0.35s ease-in-out' : 'none';
        this.track.style.transform = `translate3d(${offset + this.dragOffset}px, 0, 0)`;

        if (this.lastRenderedIndex === this.index) return;
        this.lastRenderedIndex = this.index;
        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === this.index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-current', active ? 'true' : 'false');
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        this.dots.forEach((dot, dotIndex) => {
          const active = dotIndex === this.realIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      normalizeLoop() {
        let normalized = false;
        if (this.index >= this.count * 2) {
          this.index -= this.count;
          normalized = true;
        }
        if (this.index < this.count) {
          this.index += this.count;
          normalized = true;
        }
        if (normalized) this.render(false);
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.dragging = true;
        this.pointerId = event.pointerId;
        this.pointerStart = event.clientX;
        this.dragOffset = 0;
        this.viewport.setPointerCapture(event.pointerId);
        this.track.style.transition = 'none';
      }

      onPointerMove(event) {
        if (!this.dragging || event.pointerId !== this.pointerId) return;
        this.dragOffset = event.clientX - this.pointerStart;
        this.render(false);
      }

      onPointerUp(event) {
        if (!this.dragging || event.pointerId !== this.pointerId) return;
        const threshold = Math.min(50, this.slideWidth * 0.18);
        const dragDistance = this.dragOffset;
        this.dragging = false;
        this.dragOffset = 0;

        if (Math.abs(dragDistance) >= threshold) {
          this.index += dragDistance < 0 ? 1 : -1;
        }

        this.render(true);
      }
    }
  );
}
